<template>
  <div>
    <!-- Summary bar -->
    <div
      class="d-flex align-center py-2 px-3 rounded mb-2"
      :class="allMapped ? 'success lighten-5' : 'warning lighten-5'"
    >
      <v-icon small :color="allMapped ? 'success' : 'warning'" class="mr-2">
        {{ allMapped ? "mdi-check-circle" : "mdi-alert-circle" }}
      </v-icon>
      <span class="text-body-2 font-weight-medium">
        {{ mappedCount }}/{{ totalCount }} zugeordnet
      </span>
      <v-spacer></v-spacer>
      <v-btn
        x-small
        outlined
        color="primary"
        @click="runAutomap"
        :disabled="totalCount === 0"
      >
        <v-icon x-small left>mdi-auto-fix</v-icon>
        Auto-Zuordnung
      </v-btn>
    </div>

    <!-- Loop tags banner -->
    <div
      v-for="loop in parsedPlaceholders.loops || []"
      :key="'loop-' + loop.tag"
      class="d-flex align-center px-3 py-1 mb-1 rounded grey lighten-4"
    >
      <v-icon x-small color="info" class="mr-2">mdi-repeat</v-icon>
      <code class="text-caption font-weight-medium">{{
        formatLoopTag(loop.tag)
      }}</code>
      <span class="text-caption text--secondary ml-2">Schleife</span>
    </div>

    <!-- Placeholder mapping rows -->
    <v-list v-if="placeholderTags.length > 0" dense class="pa-0">
      <template v-for="(tag, index) in placeholderTags">
        <v-divider v-if="index > 0" :key="'d-' + tag"></v-divider>
        <v-list-item :key="'item-' + tag" class="px-1">
          <v-list-item-icon class="mr-2 my-auto">
            <v-icon small :color="getStatusColor(tag)">
              {{ getStatusIcon(tag) }}
            </v-icon>
          </v-list-item-icon>

          <v-list-item-content
            class="py-2"
            style="min-width: 120px; flex: 0 0 auto"
          >
            <v-list-item-title class="text-body-2">
              <code>{{ formatTag(tag) }}</code>
            </v-list-item-title>
            <v-list-item-subtitle v-if="isAutoMapped(tag)" class="text-caption">
              automatisch erkannt
            </v-list-item-subtitle>
          </v-list-item-content>

          <v-icon small class="mx-2 text--secondary">mdi-arrow-right</v-icon>

          <v-list-item-content class="py-1" style="flex: 1 1 auto">
            <!-- Field mode: Show clickable field or button to open dialog -->
            <div v-if="isFieldMode(tag)">
              <v-btn
                v-if="getMappedFieldKey(tag)"
                text
                small
                class="text-none justify-start"
                style="width: 100%; max-width: 320px"
                @click="openFieldDialog(tag)"
              >
                <v-icon x-small left>mdi-file-tree</v-icon>
                <div class="text-left" style="flex: 1">
                  <div class="text-body-2">{{ getFieldLabel(tag) }}</div>
                  <div class="text-caption text--secondary">
                    {{ getFieldHierarchy(tag) }}
                  </div>
                </div>
                <v-icon x-small right>mdi-menu-down</v-icon>
              </v-btn>
              <v-btn
                v-else
                outlined
                small
                color="grey"
                class="text-none"
                @click="openFieldDialog(tag)"
              >
                <v-icon x-small left>mdi-plus</v-icon>
                Datenfeld zuordnen
              </v-btn>
            </div>
            <!-- Custom mode: Show chip -->
            <v-chip v-else small outlined color="grey" class="my-1">
              <v-icon x-small left>mdi-form-textbox</v-icon>
              Manuelle Eingabe
            </v-chip>
          </v-list-item-content>

          <v-list-item-action class="my-auto ml-1">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  x-small
                  v-on="on"
                  v-bind="attrs"
                  @click="toggleMode(tag)"
                >
                  <v-icon small>{{
                    isFieldMode(tag) ? "mdi-form-textbox" : "mdi-link"
                  }}</v-icon>
                </v-btn>
              </template>
              <span>{{
                isFieldMode(tag)
                  ? "Auf manuelle Eingabe umschalten"
                  : "Datenfeld zuordnen"
              }}</span>
            </v-tooltip>
          </v-list-item-action>
        </v-list-item>
      </template>
    </v-list>

    <!-- Image placeholders -->
    <div
      v-if="parsedPlaceholders.images && parsedPlaceholders.images.length > 0"
      class="mt-3"
    >
      <div class="text-overline mb-1">Bild-Platzhalter</div>
      <v-chip
        v-for="img in parsedPlaceholders.images"
        :key="img.tag"
        x-small
        class="mr-1 mb-1"
        color="purple"
        outlined
      >
        <v-icon x-small left>mdi-image</v-icon>
        {{ img.tag }}
      </v-chip>
    </div>

    <!-- Empty state -->
    <v-alert
      v-if="placeholderTags.length === 0"
      type="info"
      text
      dense
      class="mb-0"
    >
      Keine Platzhalter in der Vorlage gefunden.
    </v-alert>

    <!-- Field selection dialog -->
    <v-dialog
      v-model="fieldDialog.show"
      max-width="600"
      @click:outside="closeFieldDialog"
      @keydown.esc="closeFieldDialog"
    >
      <v-card>
        <v-card-title class="pb-2">
          <div>
            <div class="text-h6">Datenfeld zuordnen</div>
            <div class="text-caption text--secondary">
              Für Platzhalter: <code>{{ formatTag(fieldDialog.tag) }}</code>
            </div>
          </div>
          <v-spacer></v-spacer>
          <v-btn icon @click="closeFieldDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-divider></v-divider>

        <!-- Search field -->
        <v-card-text class="pb-0">
          <v-text-field
            v-model="fieldDialog.search"
            prepend-inner-icon="mdi-magnify"
            label="Suchen..."
            outlined
            dense
            hide-details
            clearable
            autofocus
            @input="filterTree"
          ></v-text-field>
        </v-card-text>

        <!-- Tree view -->
        <v-card-text class="pt-2" style="max-height: 400px; overflow-y: auto">
          <v-treeview
            :items="filteredTree"
            :active.sync="fieldDialog.active"
            :open.sync="fieldDialog.open"
            activatable
            return-object
            dense
            color="primary"
            transition
          >
            <template v-slot:prepend="{ item }">
              <v-icon v-if="item.isCategory" small>
                {{ item.icon || "mdi-folder" }}
              </v-icon>
              <v-icon v-else small color="primary">mdi-tag-outline</v-icon>
            </template>
            <template v-slot:label="{ item }">
              <div
                v-if="item.isCategory"
                class="text-body-2 font-weight-medium"
              >
                {{ item.name }}
              </div>
              <div v-else>
                <div class="text-body-2">{{ item.name }}</div>
                <div class="text-caption text--secondary">
                  {{ item.description }}
                </div>
              </div>
            </template>
          </v-treeview>

          <v-alert
            v-if="filteredTree.length === 0"
            type="info"
            text
            dense
            class="mb-0"
          >
            Keine passenden Felder gefunden.
          </v-alert>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions>
          <v-btn text @click="closeFieldDialog">Abbrechen</v-btn>
          <v-spacer></v-spacer>
          <v-btn
            v-if="fieldDialog.tag && getMappedFieldKey(fieldDialog.tag)"
            text
            color="error"
            @click="clearMapping"
          >
            <v-icon small left>mdi-delete</v-icon>
            Zuordnung entfernen
          </v-btn>
          <v-btn
            color="primary"
            :disabled="!hasValidSelection"
            @click="applyFieldSelection"
            depressed
          >
            Zuordnen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import {
  automapPlaceholders,
  getAvailableFieldsTree,
  getFieldHierarchyPath,
  getEffectiveContexts,
  ALL_FIELDS_BY_KEY,
} from "@/util/documentTemplateService.js";

export default {
  name: "placeholder-mapping-editor",
  props: {
    parsedPlaceholders: {
      type: Object,
      required: true,
      default: () => ({ simple: [], loops: [], images: [] }),
    },
    mappings: {
      type: Object,
      required: true,
    },
    placeholderContext: {
      type: String,
      required: true,
    },
  },
  model: {
    prop: "mappings",
    event: "mappingsChange",
  },
  data() {
    return {
      fieldDialog: {
        show: false,
        tag: null,
        search: "",
        active: [],
        open: [],
      },
      allTreeItems: [],
      filteredTree: [],
    };
  },
  computed: {
    mappingsLocal: {
      get() {
        return this.mappings;
      },
      set(value) {
        this.$emit("mappingsChange", value);
      },
    },
    effectiveContexts() {
      return getEffectiveContexts(this.placeholderContext);
    },
    placeholderTags() {
      const tags = [];
      (this.parsedPlaceholders.simple || []).forEach((p) => {
        if (!tags.includes(p.tag)) tags.push(p.tag);
      });
      (this.parsedPlaceholders.loops || []).forEach((loop) => {
        (loop.placeholders || []).forEach((tag) => {
          if (!tags.includes(tag)) tags.push(tag);
        });
      });
      return tags;
    },
    loopInnerTags() {
      const inner = new Set();
      (this.parsedPlaceholders.loops || []).forEach((loop) => {
        (loop.placeholders || []).forEach((tag) => inner.add(tag));
      });
      return inner;
    },
    totalCount() {
      return this.placeholderTags.length;
    },
    mappedCount() {
      return this.placeholderTags.filter((tag) => {
        const m = this.mappingsLocal[tag];
        return m && (m.status === "mapped" || m.status === "custom");
      }).length;
    },
    allMapped() {
      return this.totalCount > 0 && this.mappedCount === this.totalCount;
    },
    hasValidSelection() {
      return (
        this.fieldDialog.active.length > 0 &&
        !this.fieldDialog.active[0].isCategory
      );
    },
  },
  methods: {
    formatTag(tag) {
      return "{" + tag + "}";
    },
    formatLoopTag(tag) {
      return "{#" + tag + "} ... {/" + tag + "}";
    },
    isFieldMode(tag) {
      const m = this.mappingsLocal[tag];
      return !m || m.status !== "custom";
    },
    isAutoMapped(tag) {
      const m = this.mappingsLocal[tag];
      return m && m.autoMapped;
    },
    getMappedFieldKey(tag) {
      const m = this.mappingsLocal[tag];
      return m ? m.fieldKey : null;
    },
    getFieldLabel(tag) {
      const fieldKey = this.getMappedFieldKey(tag);
      if (!fieldKey) return "";
      const field = ALL_FIELDS_BY_KEY[fieldKey];
      return field ? field.label : fieldKey;
    },
    getFieldHierarchy(tag) {
      const fieldKey = this.getMappedFieldKey(tag);
      if (!fieldKey) return "";
      return getFieldHierarchyPath(fieldKey);
    },
    isInsideLoop(tag) {
      return this.loopInnerTags.has(tag);
    },
    getStatusColor(tag) {
      const m = this.mappingsLocal[tag];
      if (!m) return "grey";
      switch (m.status) {
        case "mapped":
          return "success";
        case "custom":
          return "info";
        case "unmapped":
          return "warning";
        default:
          return "grey";
      }
    },
    getStatusIcon(tag) {
      const m = this.mappingsLocal[tag];
      if (!m) return "mdi-help-circle-outline";
      switch (m.status) {
        case "mapped":
          return "mdi-check-circle";
        case "custom":
          return "mdi-form-textbox";
        case "unmapped":
          return "mdi-alert-circle-outline";
        default:
          return "mdi-help-circle-outline";
      }
    },
    toggleMode(tag) {
      if (this.isFieldMode(tag)) {
        this.setCustom(tag);
      } else {
        this.setFieldMode(tag);
      }
    },
    updateMapping(tag, fieldKey) {
      const newMappings = { ...this.mappingsLocal };
      if (fieldKey) {
        newMappings[tag] = {
          fieldKey,
          autoMapped: false,
          status: "mapped",
        };
      } else {
        newMappings[tag] = {
          fieldKey: null,
          autoMapped: false,
          status: "unmapped",
        };
      }
      this.mappingsLocal = newMappings;
    },
    setCustom(tag) {
      const newMappings = { ...this.mappingsLocal };
      newMappings[tag] = {
        fieldKey: null,
        autoMapped: false,
        status: "custom",
      };
      this.mappingsLocal = newMappings;
    },
    setFieldMode(tag) {
      const m = this.mappingsLocal[tag];
      if (m && m.status === "custom") {
        const newMappings = { ...this.mappingsLocal };
        newMappings[tag] = {
          fieldKey: null,
          autoMapped: false,
          status: "unmapped",
        };
        this.mappingsLocal = newMappings;
      }
    },
    runAutomap() {
      const auto = automapPlaceholders(
        this.parsedPlaceholders,
        this.effectiveContexts,
      );
      const newMappings = { ...this.mappingsLocal };
      Object.entries(auto).forEach(([tag, mapping]) => {
        const existing = newMappings[tag];
        if (!existing || existing.status === "unmapped") {
          newMappings[tag] = mapping;
        }
      });
      this.mappingsLocal = newMappings;
    },
    openFieldDialog(tag) {
      this.fieldDialog.tag = tag;
      this.fieldDialog.search = "";
      this.fieldDialog.show = true;

      // Build tree and set active
      this.allTreeItems = getAvailableFieldsTree(this.effectiveContexts);
      this.filteredTree = this.allTreeItems;

      // Pre-select current field if mapped
      const currentFieldKey = this.getMappedFieldKey(tag);
      if (currentFieldKey) {
        const selected = this.findNodeByFieldKey(
          this.allTreeItems,
          currentFieldKey,
        );
        if (selected) {
          this.fieldDialog.active = [selected];
          // Open all parent nodes
          this.openParentNodes(selected);
        }
      } else {
        this.fieldDialog.active = [];
        this.fieldDialog.open = [];
      }
    },
    closeFieldDialog() {
      this.fieldDialog.show = false;
      this.fieldDialog.tag = null;
      this.fieldDialog.search = "";
      this.fieldDialog.active = [];
      this.fieldDialog.open = [];
    },
    applyFieldSelection() {
      if (!this.hasValidSelection) return;
      const selected = this.fieldDialog.active[0];
      this.updateMapping(this.fieldDialog.tag, selected.field.key);
      this.closeFieldDialog();
    },
    clearMapping() {
      this.updateMapping(this.fieldDialog.tag, null);
      this.closeFieldDialog();
    },
    filterTree() {
      const search = (this.fieldDialog.search || "").toLowerCase().trim();
      if (!search) {
        this.filteredTree = this.allTreeItems;
        return;
      }

      // Filter tree recursively
      const filter = (items) => {
        return items.reduce((acc, item) => {
          if (item.isCategory && item.children) {
            const filteredChildren = filter(item.children);
            if (filteredChildren.length > 0) {
              acc.push({ ...item, children: filteredChildren });
            }
          } else if (!item.isCategory) {
            // Search in name, description, and full path
            const path = getFieldHierarchyPath(item.field.key).toLowerCase();
            const name = item.name.toLowerCase();
            const desc = (item.description || "").toLowerCase();
            if (
              path.includes(search) ||
              name.includes(search) ||
              desc.includes(search)
            ) {
              acc.push(item);
            }
          }
          return acc;
        }, []);
      };

      this.filteredTree = filter(this.allTreeItems);

      // Auto-open all nodes when searching
      if (search) {
        this.openAllNodes(this.filteredTree);
      }
    },
    findNodeByFieldKey(items, fieldKey) {
      for (const item of items) {
        if (!item.isCategory && item.field && item.field.key === fieldKey) {
          return item;
        }
        if (item.children) {
          const found = this.findNodeByFieldKey(item.children, fieldKey);
          if (found) return found;
        }
      }
      return null;
    },
    openParentNodes(node) {
      // Build list of all parent IDs from the node's id
      const parts = node.id.split(" > ");
      const parents = [];
      for (let i = 1; i < parts.length; i++) {
        parents.push(parts.slice(0, i).join(" > "));
      }
      // Open each parent by adding to open array
      this.fieldDialog.open = parents
        .map((id) => this.findNodeById(this.allTreeItems, id))
        .filter(Boolean);
    },
    findNodeById(items, id) {
      for (const item of items) {
        if (item.id === id) return item;
        if (item.children) {
          const found = this.findNodeById(item.children, id);
          if (found) return found;
        }
      }
      return null;
    },
    openAllNodes(items) {
      const open = [];
      const traverse = (nodes) => {
        nodes.forEach((node) => {
          if (node.isCategory) {
            open.push(node);
            if (node.children) traverse(node.children);
          }
        });
      };
      traverse(items);
      this.fieldDialog.open = open;
    },
  },
};
</script>

<style scoped>
/* No custom styles needed */
</style>
